import React, { useState } from "react";
import { useSelector } from 'react-redux';
import { format } from "date-fns";
import { useTheme, Box, Typography, Paper, Collapse,
    Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow,  
} from "@mui/material";
import { LAYOUT_HEIGHT } from "state/constants";


const LoadFactorTableau = ({ data }) => {
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const primaryDark = theme.palette.primary.dark;
    const [openCell, setOpenCell] = useState(null);

    if (!data || data.length === 0) {
        return (
            <Box p={1} sx={{ height: '100%', width: '100%' }}>        
                <Typography>Nothing to display</Typography>
            </Box>
        );
    }

    const getBgColor = (lf) => {
        if (lf === null) return "transparent";
        if (lf <= 50) return "#f2292d";
        if (lf <= 60) return "#f29129";
        if (lf <= 70) return "#d7f229";
        if (lf >= 95) return primaryDark;
        return null;
    };

    const parsed = data.map(d => {
        const [field1, field2] = d.key.split("|");
        return {
            field1,
            field2,
            lf: d.lf,
            totalPax: d.totalPax || 0,
            totalCapacity: d.totalCapacity || 0,
            flights: d.flights || []
        };
    });

    const isValidDateString = (v) =>
        typeof v === 'string' &&
        isNaN(v) && 
        !isNaN(Date.parse(v));

    const field1Values = [...new Set(parsed.map(p => p.field1))].sort((a, b) => {
        if (!isNaN(Date.parse(a)) && !isNaN(Date.parse(b))) {
            return new Date(a) - new Date(b);
        }
        return a.localeCompare(b);
    });

    const field2Values = [...new Set(parsed.map(p => p.field2))].sort();

    // overall cho toàn bảng
    const totalPaxAll = parsed.reduce((s, v) => s + (v.totalPax || 0), 0);
    const totalCapAll = parsed.reduce((s, v) => s + (v.totalCapacity || 0), 0);
    const overallLf = totalCapAll > 0 ? ((totalPaxAll / totalCapAll) * 100).toFixed(1) : null;

    return (
        <TableContainer component={Paper} sx={{ maxHeight: LAYOUT_HEIGHT }}>
            <Table stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: "bold", minWidth: "90px" }}>
                            Field
                        </TableCell>
                        {field1Values.map(field1 => (
                            <TableCell key={field1} align="center" sx={{ fontWeight: "bold" }}>
                                {isValidDateString(field1)
                                    ? format(new Date(field1), "dd.MM.yy")
                                    : field1}
                            </TableCell>
                        ))}
                        <TableCell align="center" sx={{ fontWeight: "bold" }}>
                            Average
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {field2Values.map(field2 => {
                        const rowValues = field1Values.map(field1 =>
                            parsed.find(p => p.field1 === field1 && p.field2 === field2)
                        );
                        const rowPax = rowValues.reduce((sum, v) => sum + (v?.totalPax || 0), 0);
                        const rowCap = rowValues.reduce((sum, v) => sum + (v?.totalCapacity || 0), 0);
                        const rowLf = rowCap > 0 ? ((rowPax / rowCap) * 100).toFixed(1) : null;

                        return (
                            <React.Fragment key={field2}>
                                <TableRow>
                                    <TableCell component="th" scope="row">{field2 || ""}</TableCell>
                                    {field1Values.map(field1 => {
                                        const found = parsed.find(p => p.field1 === field1 && p.field2 === field2);
                                        const cellKey = `${field1}-${field2}`;
                                        return (
                                            <TableCell
                                                key={cellKey}
                                                align="center"
                                                onClick={() => setOpenCell(openCell === cellKey ? null : cellKey)}
                                                sx={{
                                                    cursor: "pointer",
                                                    color: getBgColor(found ? found.lf : null),
                                                    backgroundColor: openCell === cellKey ? theme.palette.action.selected : "inherit",
                                                    fontWeight: openCell === cellKey ? "bold" : "normal",
                                                }}
                                            >
                                                {found ? `${found.lf}%` : "-"}
                                            </TableCell>
                                        );
                                    })}
                                    <TableCell
                                        align="center"
                                        sx={{ fontWeight: "bold", color: getBgColor(rowLf ? parseFloat(rowLf) : null) }}
                                    >
                                        {rowLf ? `${rowLf}%` : "-"}
                                    </TableCell>
                                </TableRow>

                                {/* dropdown row */}
                                {field1Values.map(field1 => {
                                    const found = parsed.find(p => p.field1 === field1 && p.field2 === field2);
                                    const cellKey = `${field1}-${field2}`;
                                    
                                    return (
                                        openCell === cellKey && found && found.flights.length > 0 && (
                                            <TableRow key={`${cellKey}-details`}>
                                                <TableCell colSpan={field1Values.length + 2} sx={{ p: 0 }}>
                                                    <Collapse in={openCell === cellKey} timeout="auto" unmountOnExit>
                                                        <Box marginLeft={2}>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow sx={{ "& .MuiTableCell-root": { color: primaryDark } }}>
                                                                        <TableCell>Date</TableCell>
                                                                        <TableCell>FlightNo</TableCell>
                                                                        <TableCell>Sector</TableCell>
                                                                        <TableCell>Etd</TableCell>
                                                                        <TableCell>Eta</TableCell>
                                                                        <TableCell>Atd</TableCell>
                                                                        <TableCell>Ata</TableCell>
                                                                        <TableCell align="right">Pax</TableCell>
                                                                        <TableCell align="right">Capacity</TableCell>
                                                                        <TableCell align="right">Load Factor</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {found.flights.map((f, idx) => {
                                                                        const flightDate = timeMode === "UTC" ? f.flightDate : f.flightDateLocalBase;
                                                                        const etd = timeMode === "UTC" ? f.etd : f.etdLocalBase;
                                                                        const eta = timeMode === "UTC" ? f.eta : f.etaLocalBase;
                                                                        const atd = timeMode === "UTC" ? f.atd : f.atdLocalBase;
                                                                        const ata = timeMode === "UTC" ? f.ata : f.ataLocalBase;
                                                                        
                                                                        return (
                                                                            <TableRow key={idx}>
                                                                                <TableCell>{format(new Date(flightDate), "dd.MM.yy")}</TableCell>
                                                                                <TableCell>{f.flightNo}</TableCell>
                                                                                <TableCell>{f.sector}</TableCell>
                                                                                <TableCell>{etd}</TableCell>
                                                                                <TableCell>{eta}</TableCell>
                                                                                <TableCell>{atd}</TableCell>
                                                                                <TableCell>{ata}</TableCell>
                                                                                <TableCell align="right">{f.pax}</TableCell>
                                                                                <TableCell align="right">{f.capacity}</TableCell>
                                                                                <TableCell align="right">{f.loadFactor}%</TableCell>
                                                                            </TableRow>
                                                                        );
                                                                    })}
                                                                </TableBody>
                                                            </Table>
                                                        </Box>
                                                    </Collapse>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}

                    {/* Overall row */}
                    <TableRow>
                        <TableCell sx={{ fontWeight: "bold" }}>Average</TableCell>
                        {field1Values.map(field1 => {
                            const colValues = parsed.filter(p => p.field1 === field1);
                            const colPax = colValues.reduce((s, v) => s + (v.totalPax || 0), 0);
                            const colCap = colValues.reduce((s, v) => s + (v.totalCapacity || 0), 0);
                            const colLf = colCap > 0 ? ((colPax / colCap) * 100).toFixed(1) : null;

                            return (
                                <TableCell
                                    key={`overall-${field1}`}
                                    align="center"
                                    sx={{
                                        fontWeight: "bold",
                                        color: getBgColor(colLf ? parseFloat(colLf) : null),
                                    }}
                                >
                                    {colLf ? `${colLf}%` : "-"}
                                </TableCell>
                            );
                        })}
                        <TableCell
                            align="center"
                            sx={{
                                fontWeight: "bold",
                                color: getBgColor(overallLf ? parseFloat(overallLf) : null),
                            }}
                        >
                            {overallLf ? `${overallLf}%` : "-"}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default LoadFactorTableau;
